import styled from 'styled-components';

import {
  REMOVE_HIGH_COST_MED_CONFIRMATION,
  removeHighCostMedRecordName,
} from 'components/highCostMeds/removeHighCostMedCopy';
import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { destroyAdminHighCostMedicationListItem } from 'services/api/admin/highCostMedicationListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type RemoveHighCostMedModalProps = {
  highCostMedicationListItem: HighCostMedicationListItem;
  onClose: () => void;
};

// Soft-archives one medication and records who removed it. Confirms in a centered modal over the
// slideout rather than in-panel: this is the confirm the design provides, and the customer surface
// shares the copy. See design 002, Admin UI.
export default function RemoveHighCostMedModal({ highCostMedicationListItem, onClose }: RemoveHighCostMedModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyAdminHighCostMedicationListItem,
  });

  const handleRemove = () => {
    mutate(
      { id: highCostMedicationListItem.id },
      {
        onSuccess: () => {
          invalidateQueries({ adminHighCostMedicationListItems: true, adminHighCostMedicationLists: true });
          addToast({ text: `${highCostMedicationListItem.genericName} successfully removed` });
          onClose();
        },
      }
    );
  };

  return (
    <StandardModal title='Delete Entry' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{removeHighCostMedRecordName(highCostMedicationListItem)}</StandardModal.RecordName>
      <Content>{REMOVE_HIGH_COST_MED_CONFIRMATION}</Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleRemove} loading={isPending} disabled={isPending}>
          Confirm
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
